/**
 * Ensures a timestamp string is treated as UTC by appending 'Z' if no timezone info is present.
 */
const ensureUtc = (dateString: string): string => {
  if (dateString.endsWith('Z') || dateString.includes('+') || /T\d{2}:\d{2}:\d{2}-/.test(dateString)) {
    return dateString;
  }
  return dateString + 'Z';
};

export const getTimestampValue = (dateString: string | null | undefined): number => {
  if (!dateString) return 0;
  return new Date(ensureUtc(dateString)).getTime();
};

/**
 * Formats a timestamp to local time (e.g., "09:12 PM")
 */
export const formatTime = (dateString: string): string => {
  return new Date(ensureUtc(dateString)).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Formats a timestamp to local date with time (e.g., "Dec 21, 2025, 06:12 PM")
 */
export const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  return new Date(ensureUtc(dateString)).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Formats a date-only string to a full display date (e.g., "Thursday, January 8, 2026")
 * Note: This is for date strings like "2026-01-08", not timestamps.
 */
export const formatDisplayDate = (dateString: string): string => {
  const d = new Date(dateString + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Formats a date-only string to a short display date (e.g., "Thu, Jan 8")
 * Note: This is for date strings like "2026-01-08", not timestamps.
 */
export const formatShortDate = (dateString: string): string => {
  const date = new Date(dateString + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};
